import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Code, Database, Server, Layout } from 'lucide-react';

const GradientBorder = ({ children }) => (
  <div className="p-[2px] rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
    <div className="h-full w-full bg-white dark:bg-gray-900 rounded-lg">
      {children}
    </div>
  </div>
);

const LayerCard = ({ title, icon: Icon, children, className }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative p-6 rounded-xl transition-all duration-300 ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-3 mb-4">
        <Icon size={24} className={`transition-all duration-300 ${isHovered ? 'scale-110' : ''}`} />
        <h2 className="text-xl font-bold">{title}</h2>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
};

const DetailBox = ({ title, items }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg transition-colors"
      >
        <span className="font-semibold">{title}</span>
        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: isExpanded ? `${contentRef.current?.scrollHeight}px` : '0' }}
      >
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li key={index} className="text-sm">
                • {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default function SophisticatedArchitecture() {
  const sections = {
    interface: {
      title: "Interface Layer",
      icon: Layout,
      color: "bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30",
      subsections: {
        controllers: {
          title: "Controllers",
          items: [
            "Orchestration des services domaine",
            "Gestion des transactions",
            "Contrôle des accès"
          ]
        },
        dtos: {
          title: "DTOs",
          items: [
            "Interfaces pour la communication",
            "Définition des contrats",
            "Abstraction des dépendances",
            "Inversion des dépendances"
          ]
        }
      }
    },
    application: {
      title: "Application Layer",
      icon: Code,
      color: "bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30",
      subsections: {
        useCases: {
          title: "Use Cases",
          items: [
            "Cas d'utilisation de l'application",
            "Orchestration des services domaine",
            "Gestion des transactions",
            "Contrôle des accès"
          ]
        },
        ports: {
          title: "Ports",
          items: [
            "Interfaces pour la communication",
            "Définition des contrats",
            "Abstraction des dépendances"
          ]
        },
        services: {
          title: "Services",
          items: [
            "Services d'orchestration",
            "Coordination des use cases",
            "Gestion des événements",
            "Monitoring et logging"
          ]
        }
      }
    },
    domain: {
      title: "Domain Layer",
      icon: Database,
      color: "bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30",
      subsections: {
        entities: {
          title: "Entities",
          items: [
            "Objets métier avec identité et cycle de vie",
            "Encapsulation des règles métier",
            "Validation des invariants",
            "Gestion du cycle de vie"
          ]
        },
        valueObjects: {
          title: "Value Objects",
          items: [
            "Objets immutables sans identité",
            "Validation à la création",
            "Encapsulation des règles de validation",
            "Immutabilité garantie"
          ]
        },
        domainServices: {
          title: "Domain Services",
          items: [
            "Services contenant la logique métier pure",
            "Logique métier complexe",
            "Orchestration des entités",
            "Règles de validation métier"
          ]
        },
        domainEvents: {
          title: "Domain Events",
          items: [
            "Événements métier significatifs",
            "Notification des changements d'état",
            "Déclenchement des workflows",
            "Traçabilité métier"
          ]
        }
      }
    },
    infrastructure: {
      title: "Infrastructure Layer",
      icon: Server,
      color: "bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30",
      subsections: {
        adapters: {
          title: "Adapters",
          items: [
            "Adaptateurs pour systèmes externes",
            "Conversion des formats",
            "Gestion des protocoles",
            "Traitement des erreurs techniques"
          ]
        },
        repositories: {
          title: "Repositories",
          items: [
            "Implémentation de la persistance",
            "Persistance des données",
            "Gestion du cache",
            "Optimisation des requêtes"
          ]
        },
        externalServices: {
          title: "External Services",
          items: [
            "Services externes",
            "Intégration externe",
            "Gestion des timeouts",
            "Circuit breakers"
          ]
        }
      }
    }
  };

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto space-y-8">
        <h1 className="text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
          Clean Architecture
        </h1>

        <div className="grid gap-8">
          {Object.entries(sections).map(([key, section]) => (
            <GradientBorder key={key}>
              <LayerCard
                title={section.title}
                icon={section.icon}
                className={section.color}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(section.subsections).map(([subKey, subsection]) => (
                    <DetailBox
                      key={subKey}
                      title={subsection.title}
                      items={subsection.items}
                    />
                  ))}
                </div>
              </LayerCard>
            </GradientBorder>
          ))}
        </div>
      </div>
    </div>
  );
}